import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import Select from "react-select";
import { PURCHASE_API } from "../../apiConfig";
import { Link, useLocation } from "react-router-dom";

// ── This page reuses the same stock-report machinery as the Stock Report
// (BatchList.js) — same /batch-list/list endpoint, same stock table — just
// always filtered to productType = "Asset", with the headers/labels shown
// as Asset Code / Asset Description instead of Product Code / Description. ──

const styles = {
  pageTitleBar: { marginBottom: "20px" },
  pageTitle: { fontSize: "28px", fontWeight: "600", color: "#0f172a", marginBottom: "4px" },
  pageSubtitle: { color: "#64748b", fontSize: "14px" },

  filterCard: {
    background: "#fff",
    borderRadius: "8px",
    padding: "15px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: "15px"
  },

  compactLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "4px",
    display: "block"
  },

  tableContainer: {
    background: "#fff",
    borderRadius: "12px",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    padding: "10px",
    border: "1px solid #e2e8f0",
    borderBottom: "none",
    overflowX: "auto"
  },

  erpTable: { marginBottom: 0, fontSize: "13px" },

  erpTh: {
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: "600",
    padding: "12px",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap"
  },

  erpTd: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    whiteSpace: "nowrap",
    color: "#334155"
  },

  cellLink: {
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: 600,
    cursor: "pointer"
  },

  paginationBar: {
    background: "#fff",
    borderRadius: "12px",
    padding: "14px 20px",
    border: "1px solid #e2e8f0",
    borderTop: "none",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  paginationInfo: { fontSize: "13px", color: "#334155" },
  paginationButtons: { display: "flex", gap: "8px" },

  paginationBtn: {
    padding: "6px 16px",
    fontSize: "13px",
    fontWeight: "600",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#334155",
    cursor: "pointer"
  },

  paginationBtnDisabled: {
    padding: "6px 16px",
    fontSize: "13px",
    fontWeight: "600",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#cbd5e1",
    cursor: "not-allowed"
  },

  pageSizeSelect: {
    padding: "6px 10px",
    fontSize: "13px",
    fontWeight: "600",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#334155",
    cursor: "pointer",
    outline: "none"
  },

  tabBarWrap: { display: "flex", justifyContent: "flex-end", marginBottom: "15px" },

  tabBar: {
    display: "inline-flex",
    gap: "2px",
    background: "#f1f5f9",
    borderRadius: "10px",
    padding: "4px"
  },

  tabBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 14px",
    fontSize: "13px",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    background: "transparent",
    color: "#64748b",
    cursor: "pointer"
  },

  tabBtnActive: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 14px",
    fontSize: "13px",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    background: "#fff",
    color: "#0f172a",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    cursor: "pointer"
  },

  tabBadge: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#2563eb",
    background: "#dbeafe",
    borderRadius: "999px",
    padding: "1px 7px",
    lineHeight: "16px"
  },

  tabBadgeInactive: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    background: "#e2e8f0",
    borderRadius: "999px",
    padding: "1px 7px",
    lineHeight: "16px"
  }
};

const cableFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: 12,
    borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
    borderRadius: 6,
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: "#94a3b8" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
  clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
  singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: 12,
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
    color: state.isSelected ? "#fff" : "#334155"
  })
};

// Strips unnecessary trailing decimal zeros from a qty value coming back
// from the DB as a fixed-precision string.
const formatQty = (qty) => {
  if (qty === null || qty === undefined || qty === "") return qty;
  const num = Number(qty);
  if (Number.isNaN(num)) return qty;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 30, 40, 50];

// Stock rows for Assets are served by a dedicated endpoint/query
// (getAssetStockList), separate from the Stock Report's getBatchList —
// which now unconditionally excludes Asset rows.

export default function AssetReport() {
  const location = useLocation();
  const [tableData, setTableData] = useState([]);

  // ── "Inspected" / "Rejected" / "Summary" view toggle, same as Stock Report ──
  const [activeTab, setActiveTab] = useState("inspected");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [batchNumberOptions, setBatchNumberOptions] = useState([]);
  const [storageLocationOptions, setStorageLocationOptions] = useState([]);

  const [filters, setFilters] = useState({
    assetCode: null,
    batchNumber: null,
    storageLocation: null
  });

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    fetchAssetStock();
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const [batchNumbersRes, storageLocationsRes] = await Promise.all([
        axios.get(`${PURCHASE_API}/batch-list/batch-numbers`),
        axios.get(`${PURCHASE_API}/batch-list/storage-locations`)
      ]);

      setBatchNumberOptions(
        (batchNumbersRes.data.data || []).map((r) => ({
          value: r.batch_number,
          label: r.batch_number
        }))
      );

      setStorageLocationOptions(
        (storageLocationsRes.data.data || []).map((r) => ({
          value: r.storage_code,
          label: `${r.storage_code} - ${r.storage_location}`
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAssetStock = async () => {
    try {
      const res = await axios.post(`${PURCHASE_API}/batch-list/asset-list`, {
        assetCode: filters.assetCode?.value || "",
        batchNumber: filters.batchNumber?.value || "",
        storageCode: filters.storageLocation?.value || ""
      });

      setTableData(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilterChange = (key, opt) => {
    setFilters((prev) => ({ ...prev, [key]: opt }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // ── Asset Code filter options — derived from the asset stock rows already
  // fetched (product_code + description), so this never depends on guessing
  // a separate assetdetails master-data endpoint/route. ──
  const assetCodeOptions = Array.from(
    new Map(
      tableData.map((r) => [
        r.product_code,
        {
          value: r.product_code,
          label: r.description ? `${r.product_code} - ${r.description}` : r.product_code
        }
      ])
    ).values()
  ).sort((a, b) => a.label.localeCompare(b.label));

  const inspectedRows = tableData.filter(
    (row) => row.quality_status === "Pending" || row.quality_status === "Inspected"
  );

  const rejectedRows = tableData.filter((row) => (row.quality_status || "").startsWith("Rejected"));

  // ── Summary tab: one line per asset code, qty summed across every
  // underlying stock row for that asset (Pending + Inspected + Rejected). ──
  const summaryRows = Object.values(
    tableData.reduce((acc, row) => {
      const key = row.product_code;
      if (!acc[key]) {
        acc[key] = {
          product_code: row.product_code,
          description: row.description,
          uom: row.uom,
          qty: 0
        };
      }
      acc[key].qty += Number(row.qty) || 0;
      return acc;
    }, {})
  );

  const activeData =
    activeTab === "inspected" ? inspectedRows : activeTab === "rejected" ? rejectedRows : summaryRows;

  const totalPages = Math.max(1, Math.ceil(activeData.length / pageSize));
  const paginatedData = activeData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const goToPrevPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid p-4">
        <div style={styles.pageTitleBar}>
          <h2 style={styles.pageTitle}>Asset Report</h2>
          <p style={styles.pageSubtitle}>
            All stock where Product Type is "Asset" — same view as the Stock Report, scoped to assets.
          </p>
        </div>

        <div style={styles.filterCard}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 0", minWidth: 200 }}>
              <label style={styles.compactLabel}>Asset Code</label>
              <Select
                placeholder="Select..."
                options={assetCodeOptions}
                value={filters.assetCode}
                onChange={(val) => handleFilterChange("assetCode", val)}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={cableFilterSelectStyles}
              />
            </div>

            <div style={{ flex: "1 1 0", minWidth: 150 }}>
              <label style={styles.compactLabel}>Asset ID</label>
              <Select
                placeholder="Select..."
                options={batchNumberOptions}
                value={filters.batchNumber}
                onChange={(val) => handleFilterChange("batchNumber", val)}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={cableFilterSelectStyles}
              />
            </div>

            <div style={{ flex: "1 1 0", minWidth: 150 }}>
              <label style={styles.compactLabel}>Storage Location</label>
              <Select
                placeholder="Select..."
                options={storageLocationOptions}
                value={filters.storageLocation}
                onChange={(val) => handleFilterChange("storageLocation", val)}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={cableFilterSelectStyles}
              />
            </div>
          </div>
        </div>

        <div style={styles.tabBarWrap}>
          <div style={styles.tabBar}>
            <button
              style={activeTab === "inspected" ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => handleTabChange("inspected")}
            >
              Inspected
              <span style={activeTab === "inspected" ? styles.tabBadge : styles.tabBadgeInactive}>
                {inspectedRows.length}
              </span>
            </button>
            <button
              style={activeTab === "rejected" ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => handleTabChange("rejected")}
            >
              Rejected
              <span style={activeTab === "rejected" ? styles.tabBadge : styles.tabBadgeInactive}>
                {rejectedRows.length}
              </span>
            </button>
            <button
              style={activeTab === "summary" ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => handleTabChange("summary")}
            >
              Summary
              <span style={activeTab === "summary" ? styles.tabBadge : styles.tabBadgeInactive}>
                {summaryRows.length}
              </span>
            </button>
          </div>
        </div>

        <div style={styles.tableContainer}>
          <div className="table-responsive">
            <table className="table" style={styles.erpTable}>
              {activeTab === "summary" ? (
                <>
                  <thead>
                    <tr>
                      <th style={styles.erpTh}>Asset Code</th>
                      <th style={styles.erpTh}>Asset Description</th>
                      <th style={styles.erpTh}>UOM</th>
                      <th style={styles.erpTh}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((row) => (
                        <tr key={row.product_code}>
                          <td style={styles.erpTd}>
                            <Link
                              to={`/cable/asset/asset-code?view=${encodeURIComponent(
                                row.product_code
                              )}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                              style={styles.cellLink}
                            >
                              {row.product_code}
                            </Link>
                          </td>
                          <td style={styles.erpTd}>{row.description || "-"}</td>
                          <td style={styles.erpTd}>{row.uom || "-"}</td>
                          <td style={styles.erpTd}>{formatQty(row.qty)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th style={styles.erpTh}>Asset Code</th>
                      <th style={styles.erpTh}>Asset Description</th>
                      <th style={styles.erpTh}>Asset ID</th>
                      <th style={styles.erpTh}>UOM</th>
                      <th style={styles.erpTh}>Qty</th>
                      <th style={styles.erpTh}>Storage Location</th>
                      <th style={styles.erpTh}>Storage Code</th>
                      <th style={styles.erpTh}>Status</th>
                      <th style={styles.erpTh}>Quality Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((row) => (
                        <tr key={row.id}>
                          <td style={styles.erpTd}>
                            <Link
                              to={`/cable/asset/asset-code?view=${encodeURIComponent(
                                row.product_code
                              )}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                              style={styles.cellLink}
                            >
                              {row.product_code}
                            </Link>
                          </td>
                          <td style={styles.erpTd}>{row.description || "-"}</td>
                          <td style={styles.erpTd}>{row.batch_number}</td>
                          <td style={styles.erpTd}>{row.uom || "-"}</td>
                          <td style={styles.erpTd}>{formatQty(row.qty)}</td>
                          <td style={styles.erpTd}>{row.storage_location}</td>
                          <td style={styles.erpTd}>{row.storage_code}</td>
                          <td style={styles.erpTd}>{row.status}</td>
                          <td style={styles.erpTd}>{row.quality_status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </div>

        <div style={styles.paginationBar}>
          <div style={styles.paginationInfo}>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> • Total {activeData.length}
          </div>

          <div style={styles.paginationButtons}>
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              style={currentPage === 1 ? styles.paginationBtnDisabled : styles.paginationBtn}
            >
              Prev
            </button>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              style={currentPage === totalPages ? styles.paginationBtnDisabled : styles.paginationBtn}
            >
              Next
            </button>

            <select value={pageSize} onChange={handlePageSizeChange} style={styles.pageSizeSelect}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </Layout>
  );
}